
import * as React from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { GOLD_PURITIES, SILVER_PURITIES, SILVER_UNITS } from "@/lib/constants"
import { useState, useEffect } from "react"
import { normalizePurity } from "@/lib/formatPurity"

interface MetalPurityModalProps {
  metal: "ORO" | "ARGENTO" | "gold" | "silver";
  // Support both naming conventions
  isOpen?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // New props
  currentPurity?: string;
  currentUnit?: string;
  onPurityChange?: (purity: string) => void;
  onUnitChange?: (unit: string) => void;
}

export function MetalPurityModal({
  metal,
  isOpen,
  onClose,
  onConfirm,
  open,
  onOpenChange,
  currentPurity,
  currentUnit,
  onPurityChange,
  onUnitChange
}: MetalPurityModalProps) {
  const isGold = metal === "ORO" || metal === "gold";
  
  // Normalize the current purity when initializing state
  const [selectedPurity, setSelectedPurity] = useState(() => {
    const defaultPurity = isGold 
      ? Object.keys(GOLD_PURITIES)[0] 
      : Object.keys(SILVER_PURITIES)[0];
    
    return currentPurity 
      ? normalizePurity(currentPurity)
      : defaultPurity;
  });
  
  const [selectedUnit, setSelectedUnit] = useState(currentUnit || 'g');
  
  // Use either GOLD_PURITIES or SILVER_PURITIES based on the metal type
  const purities = isGold ? GOLD_PURITIES : SILVER_PURITIES;
  
  useEffect(() => {
    // Update selected purity when currentPurity prop changes
    if (currentPurity) {
      setSelectedPurity(normalizePurity(currentPurity));
    }
  }, [currentPurity]);
  
  const handleSave = () => {
    if (onPurityChange) {
      console.log(`Saving purity change: ${selectedPurity}`);
      // Pass the raw selected purity value
      onPurityChange(selectedPurity);
    }
    
    if (!isGold && onUnitChange) {
      onUnitChange(selectedUnit);
    }
    
    if (onConfirm) {
      onConfirm();
    }
    
    // Handle both closing methods
    if (onClose) {
      onClose();
    } else if (onOpenChange) {
      onOpenChange(false);
    }
  };

  // Determine if modal should be open using either prop
  const dialogOpen = isOpen !== undefined ? isOpen : open;
  
  // Handle dialog close with either callback
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      if (onClose) {
        onClose();
      } else if (onOpenChange) {
        onOpenChange(false);
      }
    }
  };
  
  // Log the purity options for debugging
  console.log("Purity options:", Object.entries(purities).map(([key, { label }]) => 
    `${key} (label: ${label}, normalized: ${normalizePurity(key)})`
  ));
  console.log("Selected purity:", selectedPurity);
  
  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md select-none">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold metal-text">
            {isGold ? 'Seleziona Purezza Oro' : 'Seleziona Purezza Argento'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <div className="mb-6">
            <h3 className="text-sm font-medium mb-3">Purezza</h3>
            <RadioGroup 
              value={selectedPurity} 
              onValueChange={(value) => {
                console.log(`Radio group changed to: ${value}`);
                setSelectedPurity(value);
              }}
              className="grid grid-cols-3 gap-2"
            >
              {Object.entries(purities).map(([key, { label }]) => (
                <div key={key} className="flex items-center space-x-2">
                  <RadioGroupItem value={key} id={`purity-${key}`} />
                  <Label htmlFor={`purity-${key}`}>{label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          
          {!isGold && (
            <div>
              <h3 className="text-sm font-medium mb-3">Unità</h3>
              <RadioGroup 
                value={selectedUnit} 
                onValueChange={setSelectedUnit}
                className="flex space-x-4"
              >
                {SILVER_UNITS.map((unit) => (
                  <div key={unit} className="flex items-center space-x-2">
                    <RadioGroupItem value={unit} id={`unit-${unit}`} />
                    <Label htmlFor={`unit-${unit}`}>{unit}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}
        </div>
        
        <div className="flex justify-end">
          <Button onClick={handleSave} className="metal-accent">
            Applica
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
